import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ImportResultaat, InvoiceSource, ParsedInvoice } from '@fleet/shared';
import { fmtDate } from '@fleet/shared';
import { Alert } from '../../design-system';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { ApiError } from '../../lib/apiClient';
import { useSettings } from '../settings/settings.api';
import { InvoiceList } from './InvoiceList';
import { PendingReview } from './PendingReview';
import { UploadCards } from './UploadCards';
import type { UploadKind } from './UploadCards';
import {
  discardDocument,
  parseInvoicePdf,
  useConfirmInvoice,
  useDeleteInvoice,
  useInvoices,
} from './invoices.api';

/** Which persisted source a pending result maps to. */
function sourceVoor(parsed: ParsedInvoice): InvoiceSource {
  if (parsed.bron === 'PARSER') return 'PDF';
  if (parsed.bron === 'CSV') return 'CSV';
  return 'AI_PDF';
}

function extensieOk(_kind: UploadKind, file: File): boolean {
  return file.name.toLowerCase().endsWith('.pdf');
}

interface LaatsteImport extends ImportResultaat {
  bestandsnaam: string | null;
}

/**
 * Hoe lang de gelukt-melding blijft staan.
 *
 * Ruim, want er staan dingen in die je wilt kunnen lezen én aanklikken: de
 * pasnummers die zijn aangemaakt en de links naar die passen en naar de auto's
 * waarvan het laatste onderhoud opschoof. Een melding van vijf seconden zou die
 * links onbruikbaar maken. Hij blijft ook staan zolang je erop staat met de muis,
 * en de klok begint daarna opnieuw.
 *
 * De foutmelding erboven gaat bewust nooit weg: die vraagt een handeling.
 */
const MELDING_MS = 15_000;
const MELDING_FADE_MS = 400;

export function InvoicesPage() {
  const invoices = useInvoices();
  const settings = useSettings();
  const confirmMut = useConfirmInvoice();
  const deleteMut = useDeleteInvoice();

  const [pending, setPending] = useState<ParsedInvoice | null>(null);
  const [busy, setBusy] = useState<UploadKind | null>(null);
  const [error, setError] = useState('');
  const [laatsteImport, setLaatsteImport] = useState<LaatsteImport | null>(null);
  const [meldingUit, setMeldingUit] = useState(false);
  const [meldingVast, setMeldingVast] = useState(false);

  useEffect(() => {
    if (!laatsteImport) return;
    setMeldingUit(false);
    if (meldingVast) return;
    const fade = setTimeout(() => setMeldingUit(true), MELDING_MS);
    const weg = setTimeout(() => setLaatsteImport(null), MELDING_MS + MELDING_FADE_MS);
    return () => {
      clearTimeout(fade);
      clearTimeout(weg);
    };
  }, [laatsteImport, meldingVast]);

  async function handleFile(kind: UploadKind, file: File) {
    if (!extensieOk(kind, file)) {
      setError(`"${file.name}" is geen PDF-bestand.`);
      return;
    }
    setBusy(kind);
    setError('');
    setLaatsteImport(null);
    setPending(null);
    try {
      const parsed = await parseInvoicePdf(file, kind === 'onderhoud' ? 'ONDERHOUD' : 'MTC');
      setPending(parsed);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Het bestand kon niet worden verwerkt.');
    } finally {
      setBusy(null);
    }
  }

  async function onConfirm({ forceerDuplicaten }: { forceerDuplicaten: boolean }) {
    if (!pending) return;
    setError('');
    try {
      const res = await confirmMut.mutateAsync({
        type: pending.type,
        source: sourceVoor(pending),
        factuurnummer: pending.factuurnummer,
        datum: pending.datum,
        bestandsnaam: pending.bestandsnaam,
        regels: pending.regels,
        forceerDuplicaten,
        documentKey: pending.documentKey,
      });
      setLaatsteImport({ ...res.resultaat, bestandsnaam: pending.bestandsnaam });
      setPending(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Eyebrow>Facturen</Eyebrow>
        <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
          Kosten importeren.
        </h2>
        {/* Kort houden: wat de twee kaarten eronder aannemen en hoe ze dat doen
            staat op de kaarten zelf. Deze regel zegt alleen waar de pagina voor is. */}
        <p style={{ margin: '6px 0 0', fontSize: 14, color: '#666666', maxWidth: 720 }}>
          Hier komen de kosten binnen waar de rest van de app op rekent: de facturen van de tankpas
          en de rekeningen van de garage.
        </p>
      </div>

      <UploadCards busy={busy} aiBeschikbaar={settings.data?.aiBeschikbaar} onFile={handleFile} />

      {error && (
        <Alert tone="danger" title="Import mislukt">
          {error}
        </Alert>
      )}

      {laatsteImport && !pending && (
        <div
          onPointerEnter={() => setMeldingVast(true)}
          onPointerLeave={() => setMeldingVast(false)}
          style={{ opacity: meldingUit ? 0 : 1, transition: `opacity ${MELDING_FADE_MS}ms ease` }}
        >
          <Alert tone="success" title="Import verwerkt">
            {laatsteImport.regelsToegevoegd} regel{laatsteImport.regelsToegevoegd === 1 ? '' : 's'}{' '}
            geïmporteerd
            {laatsteImport.bestandsnaam ? <> uit {laatsteImport.bestandsnaam}</> : null}
            {laatsteImport.duplicatenOvergeslagen > 0 ? (
              <> · {laatsteImport.duplicatenOvergeslagen} duplicaten overgeslagen</>
            ) : null}
            {laatsteImport.nieuwePassen.length > 0 ? (
              <>
                {' '}
                · {laatsteImport.nieuwePassen.length} nieuwe{' '}
                <Link to="/passen">pas{laatsteImport.nieuwePassen.length === 1 ? '' : 'sen'}</Link>{' '}
                aangemaakt ({laatsteImport.nieuwePassen.join(', ')})
              </>
            ) : null}
            {laatsteImport.onderhoudBijgewerkt.length > 0 ? (
              <>
                {' '}
                · laatste onderhoud bijgewerkt:{' '}
                {laatsteImport.onderhoudBijgewerkt.map((o, i) => (
                  <span key={o.kenteken}>
                    {i > 0 ? ', ' : null}
                    <Link to={`/auto/${encodeURIComponent(o.kenteken)}`}>{o.kenteken}</Link> (
                    {o.datum ? fmtDate(o.datum) : 'gewist'})
                  </span>
                ))}
              </>
            ) : null}
          </Alert>

          {/* Een paswissel krijgt zijn eigen melding, niet een staartje achter de
              regel hierboven. Er staat nu iets in het actiecentrum te wachten, en
              wie dat niet weet komt het pas tegen als hij toevallig gaat kijken.
              Daarom staat er ook meteen waar het staat en wat er te doen is. */}
          {laatsteImport.paswisselingen?.length ? (
            <Alert
              tone="warning"
              title={`${laatsteImport.paswisselingen.length} ${
                laatsteImport.paswisselingen.length === 1 ? 'auto is' : "auto's zijn"
              } van tankpas gewisseld`}
            >
              De nieuwe pas is aan het kenteken gekoppeld, maar wie hem heeft staat niet op de
              factuur. Dat moet je bevestigen bij{' '}
              <Link to="/">Actiecentrum → Kosten zonder persoon</Link> op het dashboard, anders
              landen deze kosten op niemand.
              <span style={{ display: 'block', marginTop: 6 }}>
                {laatsteImport.paswisselingen.map((w) => (
                  <span key={`${w.kenteken}-${w.nieuwePasnummer}`} style={{ display: 'block' }}>
                    <Link to={`/auto/${encodeURIComponent(w.kenteken)}`}>{w.kenteken}</Link>: pas{' '}
                    {w.oudePasnummer} → {w.nieuwePasnummer} per {fmtDate(w.datum)}
                    {w.vermoedelijkePersoon ? ` · waarschijnlijk ${w.vermoedelijkePersoon}` : ''}
                  </span>
                ))}
              </span>
            </Alert>
          ) : null}
        </div>
      )}

      {pending && (
        <PendingReview
          pending={pending}
          onChange={setPending}
          confirming={confirmMut.isPending}
          onConfirm={onConfirm}
          onDiscard={() => {
            if (pending?.documentKey) discardDocument(pending.documentKey);
            setPending(null);
          }}
        />
      )}

      <InvoiceList
        invoices={invoices.data ?? []}
        onDelete={(id) => deleteMut.mutate(id)}
        deletingId={deleteMut.isPending ? (deleteMut.variables as string) : undefined}
      />
    </div>
  );
}
